import {
  DataTypeConfig,
  DataTypeLocator,
  DataTypeState,
  FieldDataTypeConfig,
  ListDataTypeConfig,
  ObjectDataTypeConfig,
} from '@helo/cms'
import { DataTypeConfigUtils } from '@helo/cms'
import { isKind } from '@helo/cms'
import { Assert, Deferred, Value, pipe } from '@helo/utils'
import { ColDef, ColGroupDef } from 'ag-grid-enterprise'
import { isEqual } from 'lodash'

import { Role } from '../../config/roles'
import { ExportColumn } from '../../services/queryService'

export type ColumnState = {
  key: string
  pinned: boolean
  visible: boolean
  // /**
  //  * Whether the column is disabled from the table view.
  //  */
  // disabled?: boolean
}

export type TableColumnConfig = {
  key: string
  selector: DataTypeLocator
  config?: DataTypeConfig
  state?: Record<string, unknown>
}

export type TableStateConfig = {
  columnConfigs?: TableColumnConfig[]
  columns?: ColumnState[]
  selectedRows?: string[]
}

export type TableState = {
  columnConfigs: TableColumnConfig[]
  columns: ColumnState[]
  selectedRows: string[]
}

export type TableConfig = {
  columns: {
    key: string
    selector: DataTypeLocator
  }[]
}

export type StateWithTableState = {
  _tableColumns: TableState
}

export type SavedColumnState = {
  key: string
  pinned?: boolean
}

export type SavedTableState = {
  columns: SavedColumnState[] | undefined
}

/**
 * Sorts table state columns by their visibiity.
 */
export const sortColumnsByVisibility =
  () =>
  <T extends ListDataTypeConfig>(listConfig: T): T => {
    if (!isKind('OBJECT')(listConfig.ofType))
      throw new Error('List config is not an object')

    const tableState = listConfig.state._tableColumns as TableState
    const sortedColumns = tableState.columns.sort((a, b) => {
      if (a.visible && !b.visible) return -1
      if (!a.visible && b.visible) return 1
      return 0
    })

    return {
      ...listConfig,
      state: Deferred.merge({
        _tableColumns: pipe(
          tableState ?? {},
          Deferred.merge({ sortedColumns }),
        ),
      })(listConfig.state ?? {}),
    }
  }

export const sortByPinned =
  () =>
  <T extends ListDataTypeConfig>(model: T) => {
    return {
      ...model,
      state: Deferred.merge({
        _tableColumns: pipe(
          model.state._tableColumns ?? {},
          Deferred.merge({
            columns: model.state._tableColumns.columns.sort((a, b) => {
              if (a.pinned && !b.pinned) return -1
              if (!a.pinned && b.pinned) return 1
              return 0
            }),
          }),
        ),
      })(model.state ?? {}),
    }
  }

export const colDefsToExportColumns = (colDefs: ColDef[]): ExportColumn[] =>
  colDefs
    .filter(col => !col.hide)
    .map<ExportColumn>(col => ({
      field: col.field!,
      heading: col.headerName ?? col.field!,
      typeName: (col.type as string) ?? 'String',
    }))

// export const standardizeColumnState = (
//   columns: ColumnState[] = [],
// ): ColumnState[] =>
//   pipe(columns, sortColumnsByVisibility, sortByPinned, filterByAvailableFields)

/**
 * Filter ColumnConfigs for only those available in the dataTypes
 * Filter the columns for only those available in the ColumnConfigs
 * Also filters out columns with displayDisabled=true
 * Also filters out columns with roles that the user doesn't have (if hasRole is provided)
 */
export const filterByAvailableFields =
  (hasRole?: (roles?: Role[]) => boolean) =>
  <T extends ListDataTypeConfig>(listConfig: T): T => {
    if (!isKind('OBJECT')(listConfig.ofType))
      throw new Error('List config is not an object')

    const tableState = listConfig.state._tableColumns as TableState

    const availableColumnConfigs = tableState.columnConfigs.filter(
      columnConfig => {
        // Filter out columns with displayDisabled=true
        // These columns should not render in the table but can still be used for filtering/sorting
        if (columnConfig.config?.state?.displayDisabled === true) {
          return false
        }

        // Filter out columns with roles that the user doesn't have
        if (hasRole && !hasRole(columnConfig.config?.state?.roles)) {
          return false
        }

        try {
          const dataType = pipe(
            listConfig,
            DataTypeConfigUtils.find(columnConfig.selector),
          )
          if (dataType) return true
        } catch (e) {
          return false
        }
      },
    )

    return {
      ...listConfig,
      state: {
        ...listConfig.state,
        _tableColumns: {
          ...tableState,
          columnConfigs: availableColumnConfigs,
          columns: tableState.columns.filter(col =>
            availableColumnConfigs.find(af => af.key === col.key),
          ),
        },
      },
    } as T
  }

// const fleshedOutState = (savedState: SavedColumnState[]): ColumnState[] =>
//   savedState.map(
//     (savedCol: SavedColumnState): ColumnState => ({
//       key: savedCol.key,
//       visible: true,
//       pinned: Boolean(savedCol.pinned),
//     }),
//   )

// const filterNotVisible = (columns: ColumnState[]): ColumnState[] =>
//   columns.filter(col => col.visible)

// const excludeUnpinnedKey = (columns: ColumnState[]): SavedColumnState[] =>
//   columns.map(col => ({
//     key: col.key,
//     ...(col.pinned && { pinned: col.pinned }),
//   }))

// // maybe delete.
// export const pinLeftMostColumn = (columns: ColumnState[]): ColumnState[] =>
//   columns.map((col, i) => {
//     if (i !== 0) return col

//     if (!col.visible) return col

//     return {
//       ...col,
//       pinned: true,
//     }
//   })

export const AGGridColDefUtils = {
  fromModel:
    () =>
    <T extends ListDataTypeConfig>(listConfig: T) =>
      pipe(listConfig, TableStateUtils.getTableState(), tableState =>
        tableState.columns
          .map((columnState: ColumnState): ColDef | null => {
            if (!isKind('OBJECT')(listConfig.ofType))
              throw new Error('List config is not an object')

            const columnConfig = tableState.columnConfigs.find(
              columnConfig => columnConfig.key === columnState.key,
            )

            if (!columnConfig) {
              return null
            }

            // If it's a field, get the child.
            const typeName =
              columnConfig.config?.kind === 'FIELD'
                ? columnConfig.config.ofType?.typeName
                : columnConfig.config?.typeName

            // Check if sorting is disabled for this column via state property
            const sortDisabled =
              columnConfig.config?.state.sortDisabled === true

            const gridMinWidth = columnConfig.config?.state?.minWidth
            const minWidthFromState =
              typeof gridMinWidth === 'number' ? gridMinWidth : undefined

            return {
              field: columnState.key,
              // typeName: field?.ofType?.typeName,
              // Don't set type - AG Grid doesn't recognize our custom types
              // Our type system is handled separately via ContentNodes and DataTypeConfigs
              hide: !columnState.visible,
              pinned: Boolean(columnState.pinned) && 'left',
              cellDataType: false,
              headerName:
                (columnConfig.config?.state.label as string) ??
                (columnState.key as string),
              // Move tableColumnConfig to context as AG Grid suggests for application data
              context: {
                tableColumnConfig: columnConfig,
              },
              // Disable sorting on column when sortDisabled is true
              ...(sortDisabled && { sortable: false }),
              ...(minWidthFromState != null && {
                minWidth: minWidthFromState,
              }),
            }
          })
          .filter((colDef): colDef is ColDef => colDef !== null),
      ),

  toTableState: (colDefs: (ColDef | ColGroupDef)[]): Partial<TableState> => ({
    columns: colDefs.map(
      (colDef: ColDef): ColumnState => ({
        key: colDef.field!,
        pinned: Boolean(colDef.pinned),
        visible: !colDef.hide,
      }),
    ),
    selectedRows: [],
  }),
}

const recursivelyRemoveDisabledFields = <T extends DataTypeConfig>(
  dataTypeConfig: T,
): T => {
  switch (dataTypeConfig.kind) {
    case 'OBJECT':
      return {
        ...dataTypeConfig,
        fields: dataTypeConfig.fields
          .filter(field => !field.state.disabled)
          .map(field => recursivelyRemoveDisabledFields(field)),
      }
    case 'FIELD':
    case 'INPUT_VALUE':
    case 'LIST':
      return {
        ...dataTypeConfig,
        ofType: recursivelyRemoveDisabledFields(dataTypeConfig.ofType),
      }
    case 'UNION':
      return {
        ...dataTypeConfig,
        possibleTypes: dataTypeConfig.possibleTypes.map(type =>
          recursivelyRemoveDisabledFields(type),
        ),
      }
    case 'INPUT_OBJECT':
      return {
        ...dataTypeConfig,
        fields: dataTypeConfig.fields.filter(field => !field.state.disabled),
      }
    default:
      return dataTypeConfig
  }
}

/**
 * Removes fields with disabled state. */
export const removeDisabledFields =
  () =>
  <T extends DataTypeConfig>(dataTypeConfig: T) =>
    recursivelyRemoveDisabledFields(dataTypeConfig)

export const ModelFieldUtils = {
  removeDisabledFields,
}

/**
 * Provides the default keys and column selectors based on the fields
 * in a list config's ofType's object.
 */
const getDefaultColumnConfigs = (
  listConfig: ListDataTypeConfig,
): TableColumnConfig[] => {
  if (listConfig.ofType.kind !== 'OBJECT') {
    throw new Error('List config ofType is not an object')
  }
  return listConfig.ofType.fields.map(field => ({
    key: field.key,
    selector: [['key', field.key]],
  }))
}

/**
 * Initializes the table state and ensures that all expected state exists.
 */
const initializeTableState =
  () =>
  <T extends ListDataTypeConfig>(listConfig: T): T => {
    const defaultedColumns = listConfig.state._tableColumns?.columnConfigs?.map(
      column =>
        ({
          key: column.key ?? generateColumnKeyFromLocator(column.selector),
          visible: column.visible ?? true,
          pinned: Boolean((column as any).state?.pinned ?? column.pinned),
        }) as ColumnState,
    )

    const tableState: TableState = pipe(
      { selectedRows: [] as TableState['selectedRows'] },
      Deferred.merge(
        (listConfig.state._tableColumns as Partial<TableState>) ?? {},
      ),
      Deferred.merge({ columns: defaultedColumns ?? [] }),
      Deferred.merge({
        columnConfigs:
          listConfig.state._tableColumns?.columnConfigs ??
          getDefaultColumnConfigs(listConfig),
      }),
    ) as TableState

    return {
      ...listConfig,
      state: Deferred.merge({ _tableColumns: tableState })(
        listConfig.state ?? {},
      ),
    }
  }

const generateColumnKeyFromLocator = (locator: DataTypeLocator) =>
  locator.map(part => part[part.length - 1]).join('-')

const getColumnConfigsFromTableConfig =
  (tableState: Partial<TableState>) =>
  (listConfig: ListDataTypeConfig): TableColumnConfig[] => {
    if (!tableState.columnConfigs) {
      throw new Error('Table state column configs are required')
    }

    return tableState.columnConfigs
      .map(column => {
        // filters out things that aren't found in the dataTypes
        try {
          const config = DataTypeConfigUtils.find(column.selector)(
            listConfig.ofType,
          )

          if (!config) {
            throw new Error(
              `Column ${column.key} ${column.selector.join(
                ' > ',
              )} not found in data type config`,
            )
          }
          return {
            key: column.key ?? generateColumnKeyFromLocator(column.selector),
            selector: column.selector,
            config: DataTypeConfigUtils.overwriteState(config, column),
            columnConfig: column,
          }
        } catch (e) {
          return null
        }
      })
      .filter(Value.isNotNullable)
  }

const setColumnConfigs =
  () =>
  <T extends ListDataTypeConfig>(listConfig: T): T => {
    if (!listConfig.state._tableColumns.columnConfigs) {
      throw new Error('Table state column configs are required')
    }

    return {
      ...listConfig,
      state: Deferred.merge({
        _tableColumns: pipe(
          listConfig.state._tableColumns ?? {},
          Deferred.merge({
            columnConfigs: getColumnConfigsFromTableConfig(
              listConfig.state._tableColumns,
            )(listConfig),
          }),
        ),
      })(listConfig.state ?? {}),
    }
  }

/**
 * Adds missing columns from fields as hidden.
 */
const addMissingColumns =
  () =>
  <T extends ListDataTypeConfig>(listConfig: T) => {
    if (!isKind('OBJECT')(listConfig.ofType))
      throw new Error('List config is not an object')

    const tableState = listConfig.state._tableColumns as TableState

    const missingColumns = tableState.columnConfigs
      .filter(col => !tableState.columns.find(c => c.key === col.key))
      .map(col => ({
        key: col.key,
        visible: false,
        pinned: false,
      }))

    return {
      ...listConfig,
      state: Deferred.merge({
        _tableColumns: pipe(
          tableState ?? {},
          Deferred.merge({
            columns: tableState.columns.concat(missingColumns),
          }),
        ),
      })(listConfig.state ?? {}),
    }
  }

const modelToSavedState =
  (baseModel: ListDataTypeConfig, isSavedView: boolean = false) =>
  <T extends ListDataTypeConfig>(listConfig: T): SavedTableState => {
    const tableState = listConfig.state._tableColumns as TableState
    const standardized = pipe(
      listConfig,
      // next 3 a part of standardization from before. Not sure if necessary.
      TableStateUtils.sortColumnsByVisibility(),
      TableStateUtils.sortByPinned(),
      TableStateUtils.filterByAvailableFields(),
      TableStateUtils.getTableState(),
    ) as TableState

    if (isEqual(standardized, baseModel.state._tableColumns))
      return { columns: undefined }

    // prune the columns state.
    return pipe(standardized, savedTableState => {
      return {
        columns: (savedTableState?.columns as any[])
          // remove columns that aren't visible.
          .filter(col => col.visible)
          // remove pinned property if it's not true.
          .map(col => ({
            key: col.key,
            ...(col.pinned && { pinned: col.pinned }),
          })),
      }
    })
  }

const savedStateToModel =
  (savedState: SavedTableState) =>
  <T extends ListDataTypeConfig>(baseModel: T): T => {
    // If columns are undefined,
    return pipe(
      baseModel,
      ModelStateUtils.updateState({
        _tableColumns: {
          columnConfigs: baseModel.state._tableColumns?.columnConfigs,
          columns:
            savedState.columns?.map(col => ({
              key: col.key,
              visible: true,
              pinned: Boolean(col.pinned) ?? false,
            })) ?? baseModel.state._tableColumns?.columns,
          selectedRows: [],
        },
      }),
      TableStateUtils.sortColumnsByVisibility(),
      TableStateUtils.addMissingColumns(),
      TableStateUtils.sortByPinned(),
      TableStateUtils.filterByAvailableFields(),
    ) as T
  }

/**
 * Merges a state with default state to preserve non-persisted properties.
 * (columnConfigs and selectedRows are not persisted, only columns are)
 */
const mergeWithDefaults =
  (defaultState: StateWithTableState) =>
  (state: StateWithTableState): StateWithTableState =>
    pipe(
      defaultState ?? {},
      Deferred.merge(state ?? {}),
      Deferred.merge({
        _tableColumns: pipe(
          defaultState?._tableColumns ?? {},
          Deferred.merge(state?._tableColumns ?? {}),
        ),
      }),
    )

/**
 * Extracts all field keys from an ObjectDataTypeConfig as TableColumnConfig entries.
 * Creates a base set of columns from the data model.
 */
const extractColumnsFromObjectConfig = (
  objectConfig: ObjectDataTypeConfig,
): TableColumnConfig[] => {
  // ObjectDataTypeConfig has a fields property directly
  const fields = objectConfig.fields

  return fields.map(field => ({
    key: field.key,
    selector: [['key', field.key]],
    config: field,
  }))
}

/**
 * Merges auto-discovered columns with CMS-provided columnConfigs.
 *
 * Logic:
 * - CMS columnConfigs override auto-discovered columns (for labels, state)
 * - Fields in CMS config are visible by default
 * - Fields NOT in CMS config are HIDDEN by default
 * - Preserves CMS config ordering for defined columns
 */
const mergeColumnsWithCMSConfig = (
  autoDiscoveredColumns: TableColumnConfig[],
  cmsColumnConfigs?: TableColumnConfig[],
): { columnConfigs: TableColumnConfig[]; defaultColumns: ColumnState[] } => {
  if (!cmsColumnConfigs || cmsColumnConfigs.length === 0) {
    // No CMS config - all columns visible by default
    return {
      columnConfigs: autoDiscoveredColumns,
      defaultColumns: autoDiscoveredColumns.map(col => ({
        key: col.key,
        visible: true,
        pinned: false,
      })),
    }
  }

  // Create a map for matching CMS configs by selector
  const cmsConfigMap = new Map<string, TableColumnConfig>()
  cmsColumnConfigs.forEach(cmsConfig => {
    const selectorKey = JSON.stringify(cmsConfig.selector)
    cmsConfigMap.set(selectorKey, cmsConfig)
  })

  const mergedColumnConfigs: TableColumnConfig[] = []
  const defaultColumns: ColumnState[] = []

  // First: Add CMS-defined columns in CMS order (visible by default)
  cmsColumnConfigs.forEach(cmsConfig => {
    const selectorKey = JSON.stringify(cmsConfig.selector)
    const autoColumn = autoDiscoveredColumns.find(
      auto => JSON.stringify(auto.selector) === selectorKey,
    )

    if (autoColumn) {
      // Merge: preserve auto-discovered config, apply CMS state/overrides
      const merged: TableColumnConfig = {
        ...autoColumn,
        ...cmsConfig,
        config: autoColumn.config, // Keep the full field config
        state: pipe(
          autoColumn.config?.state ?? {},
          Deferred.merge(cmsConfig.state ?? {}),
        ),
      }
      mergedColumnConfigs.push(merged)

      defaultColumns.push({
        key: merged.key,
        visible: true,
        pinned: Boolean(cmsConfig.state?.pinned),
      })
    }
  })

  // Second: Add auto-discovered columns NOT in CMS as hidden
  autoDiscoveredColumns.forEach(autoColumn => {
    const selectorKey = JSON.stringify(autoColumn.selector)
    if (!cmsConfigMap.has(selectorKey)) {
      mergedColumnConfigs.push(autoColumn)
      defaultColumns.push({
        key: autoColumn.key,
        visible: false, // Hidden by default
        pinned: false,
      })
    }
  })

  return { columnConfigs: mergedColumnConfigs, defaultColumns }
}

export const TableColumnConfigUtils = {
  extractColumnsFromObjectConfig,
  mergeColumnsWithCMSConfig,
}

/**
 * Constructs columns from object-type fields that have their own table states.
 * Overwrites the listDataTypeConfig's columns property in the table state with
 * columns based on list types of type object fields that have their own states.
 */
const constructColumnsFromObjectFields =
  () =>
  <T extends ListDataTypeConfig>(listConfig: T): T => {
    const fields = pipe(
      listConfig,
      DataTypeConfigUtils.getOfType(),
      Assert.orThrow(DataTypeConfigUtils.isKind('OBJECT')),
      DataTypeConfigUtils.getFields(),
    ) as FieldDataTypeConfig[]

    const columns: ColumnState[] = fields.map(field => ({
      key: field.key,
      visible: true,
      pinned: false,
    }))

    return pipe(
      listConfig,
      ModelStateUtils.updateState({
        _tableColumns: {
          columns,
        },
      }),
    ) as T
  }

const autosizeAllVisibleColumnKeys = (columns: ColumnState[]): string[] =>
  columns.filter(column => column.visible).map(column => column.key)

export type ColumnMenuRevisionInput = {
  /** Applied filter object data (not the full content-node identity). */
  filterData: Record<string, unknown>
  /** Filterable column field keys currently available in the header menu. */
  filterFieldKeys: string[]
  colorMode?: 'light' | 'dark' | null
  /**
   * Field key of the column filter card that is open, if any.
   * Required for controlled `isPopoverOpen` in AG Grid headers.
   */
  openColumnFilterField?: string | null
}

/**
 * Builds the serialized `columnMenuRevision` value passed from `TableWidget` to
 * `Table` and into AG Grid header context.
 *
 * AG Grid custom header components do not re-render when the host's React state
 * changes. `Table` calls `api.refreshHeader()` only when this revision string
 * changes. That means every input that affects what the column-menu slot should
 * render — or how a controlled `ColumnFilterEditor` should look/behave — must be
 * represented here.
 *
 * Include:
 * - `filterData` / `filterFieldKeys` / `colorMode` when applied filters, menu
 *   availability, or styling inputs change.
 * - `openColumnFilterField` when the table coordinates a single open column
 *   filter via controlled `isPopoverOpen` (CFE-R-1 in
 *   `ColumnFilterEditor/requirements.mdc`).
 *
 * Exclude:
 * - Equivalent content-node object identity churn (e.g. row loads that rebuild
 *   query variables without changing applied filter data). Serializing meaningful
 *   filter data instead of node references keeps unrelated refreshes from
 *   remounting open cards (CFE-R-2 / CFE-R-3).
 *
 * Full requirement: CFE-R-23 in `ColumnFilterEditor/requirements.mdc`.
 */
const buildColumnMenuRevision = (input: ColumnMenuRevisionInput): string =>
  JSON.stringify({
    filterData: input.filterData,
    filterFields: input.filterFieldKeys,
    colorMode: input.colorMode ?? null,
    openColumnFilterField: input.openColumnFilterField ?? null,
  })

/** Utilities for AG Grid column-header menu refresh wiring in `TableWidget`. */
export const ColumnMenuUtils = {
  buildColumnMenuRevision,
}

export const TableStateUtils = {
  getTableState:
    () =>
    <T extends ListDataTypeConfig>(listConfig: T) =>
      listConfig.state._tableColumns as TableState,
  initializeTableState,
  setColumnConfigs,
  sortColumnsByVisibility,
  sortByPinned,
  filterByAvailableFields,
  addMissingColumns,
  toSavedState: modelToSavedState,
  fromSavedState: savedStateToModel,
  mergeWithDefaults,
  constructColumnsFromObjectFields,
  autosizeAllVisibleColumnKeys,
}

export const ModelStateUtils = {
  /**
   * Replaces state.
   */
  setState:
    (state: DataTypeState) =>
    <T extends DataTypeConfig>(model: T): T => ({
      ...model,
      state,
    }),
  getState:
    () =>
    <T extends DataTypeConfig>(model: T) =>
      model.state,
  /**
   * Shallow merges state.
   */
  updateState:
    (updatedState: Partial<DataTypeState>) =>
    <T extends DataTypeConfig>(model: T): T => {
      return {
        ...model,
        state: pipe(
          model.state ?? {},
          Deferred.merge(updatedState ?? {}),
          Deferred.merge({
            _tableColumns: pipe(
              model.state?._tableColumns ?? {},
              Deferred.merge(updatedState?._tableColumns ?? {}),
            ),
          }),
        ),
      }
    },
}

// So, to target placeRole Origin or Lading, I'd have to be able to get a single item
// via a filter.

// This is possible by adding a ContentNode List filter (and dataTypeConfig filter that ignores it).

// I will also have to figure out how to apply the filter automatically in addition to filters on that field
// for network requests.

// For instance. I only need to filter by Origin for the contentNode to find that placerol for the table
// or other kind of display.  That's not too bad (though, if not found, it'll need to return an
// empty content node.).

// If I'm using a filter for that column selector, it'll be able to filter on a PlaceRole, but
// will also need to be able to filter by other values. So, I'd need to automatically include the roelTypeName origin
// in a filter that also wants to do a root level filter based on the Place DisplayValue. These would
// need to work automatically in a nested way..... very fucking complicated, but I think it's doable.
//   `)
